import { IUser, UserRole } from "@core/interfaces";
import { Company } from "./company.entity";
import { Entity } from "./entity";

export class User extends Entity<IUser> {
    get id() {
        return this.props.id;
    }

    get name() {
        return this.props.name;
    }

    get shortName() {
        return this.props.name?.split(' ').reduce((combinedNamePart, namePart) => combinedNamePart + namePart[0], '')?.toUpperCase();
    }

    get mobile() {
        return this.props.mobile;
    }

    get email() {
        return this.props.email;
    }

    get role() {
        return this.props.role;
    }

    get company() {
        return this.props.company && new Company(this.props.company);
    }

    get isMobileVerified() {
        return this.props.isMobileVerified;
    }

    get isEmailVerified() {
        return this.props.isEmailVerified;
    }

    get isOnboarded() {
        return this.props.isOnboarded;
    }

    get status() {
        return this.props.status;
    }

    get isUploader(): boolean {
        return this.props.role === UserRole.Uploader;
    }

    get isReviewer(): boolean {
        return this.props.role === UserRole.Reviewer;
    }

    get isOutlet(): boolean {
        return this.props.role === UserRole.Outlet;
    }

    get isOwner(): boolean {
        return this.props.role === UserRole.Owner;
    }

    get isAdmin(): boolean {
        return this.props.role === UserRole.Admin;
    }

    get isLeadAdmin(): boolean {
        return this.props.role === UserRole.LeadAdmin;
    }

    get isChildLeadAdmin(): boolean {
        return this.props.role === UserRole.LeadAdmin && !!this.props.parent;
    }

    get parent(): string | undefined {
        return this.props.parent;
    }

    get isSuperAdmin(): boolean {
        return this.props.role === UserRole.SuperAdmin;
    }

    get isManager(): boolean {
        return this.props.isSecondaryOwner && this.props.secondaryOwnerRole === 'Manager';
    }

    get buyerSummary() {
        return this.props.buyerSummary;
    }

    get reviewerSummary() {
        return this.props.reviewerSummary;
    }

    get userAccessList() {
        return this.props.userAccessList;
    }

    get isSecondaryOwner() {
        return this.props.isSecondaryOwner;
    }

    get secondaryOwnerRole() {
        return this.props.secondaryOwnerRole;
    }

    get isDemo() {
        return this.props.isDemo;
    }

    get isNarration() {
        return this.props.isNarration;
    }

    get thumbnail() {
        return this.props.thumbnail;
    }

    get profileSetupCurrentPage() {
        return this.props.profileSetupCurrentPage;
    }

    get profileSetupCompanyPageSkip() {
        return this.props.profileSetupCompanyPageSkip;
    }

    get freeTrialStartDateTime() {
        return this.props.freeTrialStartDateTime;
    }

    get isPaidUser() {
        return this.props.isPaidUser;
    }

    get freeTrialDays() {
        return this.props.freeTrialDays;
    }

    get remainingFreeTrialDays() {
        return Number(10 - this.props.freeTrialDays) < 0 ? 0 : Number(10 - this.props.freeTrialDays).toFixed(0);
    }

    get invoiceCount() {
        return this.props.invoiceCount;
    }

    set isNarration(flag: boolean) {
        this.props.isNarration = flag;
    }

    set thumbnail(thumbnail: string) {
        this.props.thumbnail = thumbnail;
    }

    set name(name: string) {
        this.props.name = name;
    }

    set email(email: string) {
        this.props.email = email;
    }

    set profileSetupCurrentPage(page: string) {
        this.props.profileSetupCurrentPage = page;
    }

    set profileSetupCompanyPageSkip(isSkipped: boolean) {
        this.props.profileSetupCompanyPageSkip = isSkipped;
    }

    set freeTrialStartDateTime(freeTrialStartDateTime: string) {
        this.props.freeTrialStartDateTime = freeTrialStartDateTime;
    }

    set isPaidUser(isPaidUser: boolean) {
        this.props.isPaidUser = isPaidUser;
    }

    set freeTrialDays(freeTrialDays: number) {
        this.props.freeTrialDays = freeTrialDays;
    }

    set invoiceCount(invoiceCount: number) {
        this.props.invoiceCount = invoiceCount;
    }

    get enablePurchase() {
        return this.props.enablePurchase;
    }

    withAccess(access: { userAccessList?: string[]; enablePurchase?: boolean }) {
        return new User({
            ...this.props,
            userAccessList: access.userAccessList ?? this.props.userAccessList,
            enablePurchase: typeof access.enablePurchase === 'boolean' ? access.enablePurchase : this.props.enablePurchase
        });
    }

    get isHyperpureEnabled() {
        return this.props.isHyperpureEnabled;
    }
}